import { useEffect, useRef, useState } from "react";
import {
    AlertCircle,
    Check,
    ChevronDown,
    ChevronRight,
    Eye,
    EyeOff,
    RotateCcw,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuLabel,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useUserProfile } from "@/contexts/UserProfileContext";
import type { ApiKeyState } from "@/app/lib/auditflowApi";
import { MODELS } from "@/app/components/assistant/ModelToggle";
import {
    isModelAvailable,
    modelGroupToProvider,
    providerLabel,
} from "@/app/lib/modelAvailability";

const API_KEY_FIELDS = [
    {
        provider: "claude",
        label: "Anthropic (Claude) API Key",
        placeholder: "sk-ant-…",
    },
    {
        provider: "gemini",
        label: "Google (Gemini) API Key",
        placeholder: "AI…",
    },
    {
        provider: "openai",
        label: "OpenAI API Key",
        placeholder: "sk-…",
    },
    {
        provider: "openrouter",
        label: "OpenRouter API Key",
        placeholder: "sk-or-…",
        description:
            "Use a single OpenRouter key to access all models when a direct provider key is not configured.",
    },
] as const;

const DEFAULT_PROMPT_TEMPLATE =
    'You are a legal document analysis assistant. Based on the document text provided, suggest {{num_questions}} concise, insightful questions a lawyer would ask about this document. Return ONLY valid JSON in this exact format: {"suggestions":["question 1","question 2","question 3","question 4"]}. No other text.';

const PROMPT_VARIABLES = [
    "document_text",
    "num_questions",
    "filename",
    "document_type",
    "project_name",
    "active_language",
    "word_count",
];

export default function ModelsAndApiKeysPage() {
    const {
        profile,
        updateModelPreference,
        updateSuggestionPromptTemplate,
        updateApiKey,
    } = useUserProfile();

    return (
        <div className="space-y-4">
            {/* Model Preferences */}
            <div className="pb-6">
                <div className="flex items-center gap-2 mb-4">
                    <h2 className="text-2xl font-medium font-serif">
                        Model Preferences
                    </h2>
                </div>
                <div className="space-y-6 max-w-2xl">
                    {/* Chat Assistant Default */}
                    <div>
                        <label className="text-sm text-gray-600 block mb-1">
                            Chat assistant default model
                        </label>
                        <p className="text-xs text-gray-400 mb-2">
                            Default model used when starting a new chat. You
                            can still override per message.
                        </p>
                        <ModelDropdown
                            value={
                                profile?.chatModel ??
                                "gemini-3-flash-preview"
                            }
                            apiKeys={profile?.apiKeys}
                            onChange={(id) =>
                                updateModelPreference("chatModel", id)
                            }
                        />
                    </div>

                    {/* Tabular Review */}
                    <div>
                        <label className="text-sm text-gray-600 block mb-1">
                            Tabular review model
                        </label>
                        <p className="text-xs text-gray-400 mb-2">
                            We recommend using a smaller model for tabular
                            reviews to reduce token costs.
                        </p>
                        <ModelDropdown
                            value={
                                profile?.tabularModel ??
                                "gemini-3-flash-preview"
                            }
                            apiKeys={profile?.apiKeys}
                            onChange={(id) =>
                                updateModelPreference("tabularModel", id)
                            }
                        />
                    </div>

                    {/* Suggested Questions */}
                    <div>
                        <label className="text-sm text-gray-600 block mb-1">
                            Suggested questions model
                        </label>
                        <p className="text-xs text-gray-400 mb-2">
                            Model used to generate contextual question chips
                            from documents.
                        </p>
                        <ModelDropdown
                            value={
                                profile?.suggestionModel ??
                                "gemini-3-flash-preview"
                            }
                            apiKeys={profile?.apiKeys}
                            onChange={(id) =>
                                updateModelPreference("suggestionModel", id)
                            }
                        />
                    </div>

                    {/* Suggestion Prompt Template */}
                    <SuggestionPromptEditor
                        value={
                            profile?.suggestionPromptTemplate ??
                            DEFAULT_PROMPT_TEMPLATE
                        }
                        onSave={updateSuggestionPromptTemplate}
                    />
                </div>
            </div>

            {/* API Keys */}
            <div className="py-6">
                <div className="flex items-center gap-2 mb-2">
                    <h2 className="text-2xl font-medium font-serif">
                        API Keys
                    </h2>
                </div>
                <p className="text-sm text-gray-500 mb-4 max-w-xl">
                    You must provide your own API keys for the app to work or
                    add your API keys into the .env file if you are running your
                    own instance of AuditFlow.
                </p>
                <p className="text-xs text-gray-400 mb-4 max-w-xl">
                    Title generation automatically routes to the cheapest
                    configured provider model.
                </p>
                <div className="space-y-4 max-w-xl">
                    {API_KEY_FIELDS.map((field) => (
                        <ApiKeyField
                            key={field.provider}
                            label={field.label}
                            placeholder={field.placeholder}
                            description={
                                "description" in field
                                    ? field.description
                                    : undefined
                            }
                            hasSavedKey={
                                !!profile?.apiKeys[field.provider].configured
                            }
                            isServerConfigured={
                                profile?.apiKeys[field.provider].source ===
                                "env"
                            }
                            onSave={(value) =>
                                updateApiKey(
                                    field.provider,
                                    value.trim() || null,
                                )
                            }
                            onRemove={() =>
                                updateApiKey(field.provider, null)
                            }
                        />
                    ))}
                </div>
            </div>
        </div>
    );
}

function ModelDropdown({
    value,
    onChange,
    apiKeys,
}: {
    value: string;
    onChange: (id: string) => void;
    apiKeys?: ApiKeyState;
}) {
    const [isOpen, setIsOpen] = useState(false);
    const selected = MODELS.find((m) => m.id === value);
    const selectedAvailable = apiKeys ? isModelAvailable(value, apiKeys) : true;
    const groups: ("Anthropic" | "Google" | "OpenAI" | "Kimi")[] = [
        "Anthropic",
        "Google",
        "OpenAI",
        "Kimi",
    ];

    return (
        <DropdownMenu onOpenChange={setIsOpen}>
            <DropdownMenuTrigger asChild>
                <button
                    type="button"
                    className="w-full h-9 rounded-md border border-gray-300 bg-white px-3 text-sm shadow-sm flex items-center justify-between gap-2 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-black/10"
                >
                    <span className="flex items-center gap-2 min-w-0">
                        {!selectedAvailable && (
                            <AlertCircle className="h-3.5 w-3.5 shrink-0 text-red-500" />
                        )}
                        <span className="truncate text-gray-900">
                            {selected?.label ?? "Select a model"}
                        </span>
                    </span>
                    <ChevronDown
                        className={`h-3.5 w-3.5 shrink-0 text-gray-500 transition-transform duration-200 ${isOpen ? "rotate-180" : ""}`}
                    />
                </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent
                className="z-50"
                style={{ width: "var(--radix-dropdown-menu-trigger-width)" }}
                align="start"
            >
                {groups.map((group, gi) => {
                    const items = MODELS.filter((m) => m.group === group);
                    if (items.length === 0) return null;
                    return (
                        <div key={group}>
                            {gi > 0 && <DropdownMenuSeparator />}
                            <DropdownMenuLabel className="text-[10px] uppercase tracking-wider text-gray-400">
                                {group}
                            </DropdownMenuLabel>
                            {items.map((m) => {
                                const provider = modelGroupToProvider(m.group);
                                const available = apiKeys
                                    ? isModelAvailable(m.id, apiKeys)
                                    : true;
                                return (
                                    <DropdownMenuItem
                                        key={m.id}
                                        className="cursor-pointer"
                                        onSelect={() => onChange(m.id)}
                                        title={
                                            !available
                                                ? `Add a ${providerLabel(provider)} API key to use this model`
                                                : undefined
                                        }
                                    >
                                        <span
                                            className={`flex-1 ${available ? "" : "text-gray-400"}`}
                                        >
                                            {m.label}
                                        </span>
                                        {!available && (
                                            <AlertCircle className="h-3.5 w-3.5 text-red-500 ml-1" />
                                        )}
                                        {m.id === value && available && (
                                            <Check className="h-3.5 w-3.5 text-gray-600 ml-1" />
                                        )}
                                    </DropdownMenuItem>
                                );
                            })}
                        </div>
                    );
                })}
            </DropdownMenuContent>
        </DropdownMenu>
    );
}

function SuggestionPromptEditor({
    value,
    onSave,
}: {
    value: string;
    onSave: (template: string) => Promise<boolean>;
}) {
    const [draft, setDraft] = useState(value);
    const [saving, setSaving] = useState(false);
    const [saved, setSaved] = useState(false);
    const [previewOpen, setPreviewOpen] = useState(false);
    const textareaRef = useRef<HTMLTextAreaElement>(null);

    useEffect(() => {
        setDraft(value);
    }, [value]);

    const missingDocumentText = !draft.includes("{{document_text}}");

    const insertVariable = (varName: string) => {
        const el = textareaRef.current;
        if (!el) return;
        const start = el.selectionStart;
        const end = el.selectionEnd;
        const before = draft.slice(0, start);
        const after = draft.slice(end);
        const next = `${before}{{${varName}}}${after}`;
        setDraft(next);
        requestAnimationFrame(() => {
            el.focus();
            const pos = start + varName.length + 4; // {{ }}
            el.setSelectionRange(pos, pos);
        });
    };

    const handleReset = () => {
        setDraft(DEFAULT_PROMPT_TEMPLATE);
    };

    const handleSave = async () => {
        setSaving(true);
        const ok = await onSave(draft);
        setSaving(false);
        if (ok) {
            setSaved(true);
            setTimeout(() => setSaved(false), 2000);
        }
    };

    const preview = draft
        .replace(/{{document_text}}/g, "[truncated document text...]")
        .replace(/{{num_questions}}/g, "4")
        .replace(/{{filename}}/g, "contract.pdf")
        .replace(/{{document_type}}/g, "PDF")
        .replace(/{{project_name}}/g, "Matter 2024-001")
        .replace(/{{active_language}}/g, "pt-BR")
        .replace(/{{word_count}}/g, "4,231");

    return (
        <div className="border-t border-gray-200 pt-6">
            <div className="flex items-center justify-between mb-2">
                <label className="text-sm text-gray-600 block">
                    Suggestion prompt template
                </label>
                <button
                    type="button"
                    onClick={handleReset}
                    className="flex items-center gap-1 text-xs text-gray-500 hover:text-gray-700 transition-colors"
                >
                    <RotateCcw className="h-3 w-3" />
                    Reset to default
                </button>
            </div>
            <p className="text-xs text-gray-400 mb-2">
                Customize the prompt used to generate question suggestions.
                Supports variables.
            </p>

            {/* Variable chips */}
            <div className="flex flex-wrap gap-1.5 mb-2">
                {PROMPT_VARIABLES.map((v) => (
                    <button
                        key={v}
                        type="button"
                        onClick={() => insertVariable(v)}
                        className="text-[10px] px-1.5 py-0.5 rounded bg-gray-100 text-gray-600 hover:bg-gray-200 transition-colors"
                    >
                        {"{{"}{v}{"}}"}
                    </button>
                ))}
            </div>

            <textarea
                ref={textareaRef}
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                rows={6}
                className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm font-mono text-gray-800 focus:outline-none focus:ring-2 focus:ring-black/10 resize-y"
                spellCheck={false}
            />

            {missingDocumentText && (
                <p className="mt-1.5 text-xs text-red-500">
                    Template must include{" "}
                    <code className="bg-red-50 px-1 rounded">{"{{document_text}}"}</code>.
                    The server will fall back to the default if missing.
                </p>
            )}

            <div className="flex items-center gap-3 mt-3">
                <Button
                    onClick={handleSave}
                    disabled={saving || saved}
                    className="min-w-[80px] transition-all bg-black hover:bg-gray-900 text-white"
                >
                    {saving
                        ? "Saving..."
                        : saved
                          ? (
                              <>
                                  <Check className="h-3.5 w-3.5 mr-1" />
                                  Saved
                              </>
                          )
                          : "Save template"}
                </Button>
                <button
                    type="button"
                    onClick={() => setPreviewOpen((v) => !v)}
                    className="flex items-center gap-1 text-xs text-gray-500 hover:text-gray-700 transition-colors"
                >
                    <ChevronRight
                        className={`h-3 w-3 transition-transform ${previewOpen ? "rotate-90" : ""}`}
                    />
                    Preview rendered prompt
                </button>
            </div>

            {previewOpen && (
                <div className="mt-3 rounded-md border border-gray-200 bg-gray-50 p-3">
                    <p className="text-[10px] uppercase tracking-wider text-gray-400 mb-1">
                        Preview
                    </p>
                    <pre className="text-xs font-mono text-gray-700 whitespace-pre-wrap">
                        {preview}
                    </pre>
                </div>
            )}
        </div>
    );
}

function ApiKeyField({
    label,
    placeholder,
    description,
    hasSavedKey,
    isServerConfigured,
    onSave,
    onRemove,
}: {
    label: string;
    placeholder: string;
    description?: string;
    hasSavedKey: boolean;
    isServerConfigured: boolean;
    onSave: (value: string) => Promise<boolean>;
    onRemove: () => Promise<boolean>;
}) {
    const [value, setValue] = useState("");
    const [reveal, setReveal] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [saved, setSaved] = useState(false);

    useEffect(() => {
        setValue("");
    }, [hasSavedKey]);

    const dirty = value.trim().length > 0;

    const handleSave = async () => {
        setIsSaving(true);
        const ok = await onSave(value);
        setIsSaving(false);
        if (ok) {
            setValue("");
            setSaved(true);
            setTimeout(() => setSaved(false), 2000);
        } else {
            alert(`Failed to save ${label}.`);
        }
    };

    const handleRemove = async () => {
        setIsSaving(true);
        const ok = await onRemove();
        setIsSaving(false);
        if (!ok) alert(`Failed to remove ${label}.`);
    };

    return (
        <div>
            <label className="text-sm text-gray-600 block mb-2">{label}</label>
            {description && (
                <p className="text-xs text-gray-400 mb-2">{description}</p>
            )}
            {isServerConfigured && (
                <div className="mb-2 rounded-md border border-blue-100 bg-blue-50 px-3 py-2">
                    <p className="text-xs text-blue-800">
                        A server .env key is configured for this provider.
                        Browser API-key edits are disabled.
                    </p>
                    {hasSavedKey && (
                        <p className="mt-1 text-xs text-blue-800">
                            The server key will be used for this provider.
                        </p>
                    )}
                </div>
            )}
            {hasSavedKey && !isServerConfigured && (
                <p className="text-xs text-gray-500 mb-2">
                    A key is saved. Paste a new key to replace it.
                </p>
            )}
            <div className="flex gap-2">
                <div className="relative flex-1">
                    <Input
                        type={reveal ? "text" : "password"}
                        value={value}
                        onChange={(e) => setValue(e.target.value)}
                        placeholder={
                            isServerConfigured
                                ? "Server .env key configured"
                                : hasSavedKey
                                  ? "Saved key hidden"
                                  : placeholder
                        }
                        className="pr-10"
                        autoComplete="off"
                        spellCheck={false}
                        disabled={isServerConfigured}
                    />
                    <button
                        type="button"
                        onClick={() => setReveal((r) => !r)}
                        disabled={isServerConfigured}
                        className="absolute inset-y-0 right-2 flex items-center text-gray-400 hover:text-gray-600 disabled:cursor-not-allowed disabled:opacity-40"
                        aria-label={reveal ? "Hide key" : "Show key"}
                    >
                        {reveal ? (
                            <EyeOff className="h-4 w-4" />
                        ) : (
                            <Eye className="h-4 w-4" />
                        )}
                    </button>
                </div>
                <Button
                    onClick={handleSave}
                    disabled={isServerConfigured || isSaving || !dirty || saved}
                    className="min-w-[80px] transition-all bg-black hover:bg-gray-900 text-white"
                >
                    {isSaving ? (
                        "Saving..."
                    ) : saved ? (
                        <>
                            <Check className="h-4 w-3" />
                            Saved
                        </>
                    ) : (
                        "Save"
                    )}
                </Button>
                {hasSavedKey && !isServerConfigured && (
                    <Button
                        type="button"
                        variant="outline"
                        onClick={handleRemove}
                        disabled={isSaving}
                    >
                        Remove
                    </Button>
                )}
            </div>
        </div>
    );
}
